import { useState, useEffect, useCallback } from 'react'
import { Capacitor } from '@capacitor/core'

// Only import types; these will be erased at compile time
import type {
  PurchasesOfferings,
  PurchasesPackage,
  CustomerInfo,
} from '@revenuecat/purchases-capacitor'
import { useAuth } from '../context/AuthContext'
import fetchWithAuth from '../utils/fetchWithAuth'

// Detect if running in native (iOS/Android) or web
const isNative = Capacitor.getPlatform() !== 'web'

// Lazy-load native plugins only on mobile
let Purchases: typeof import('@revenuecat/purchases-capacitor').Purchases
let RevenueCatUI: typeof import('@revenuecat/purchases-capacitor-ui').RevenueCatUI
if (isNative) {
  // @ts-ignore
  Purchases = require('@revenuecat/purchases-capacitor').Purchases
  // @ts-ignore
  RevenueCatUI = require('@revenuecat/purchases-capacitor-ui').RevenueCatUI
}

/**
 * Hook to interact with RevenueCat purchases.
 * On web, all methods become no-op or return null.
 */
export function useRevenueCat() {
  const { user: firebaseUser } = useAuth()
  const [initialized, setInitialized] = useState(false)
  const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null)
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null)

  // Initialize SDK once on native (after we have a user)
  useEffect(() => {
    if (!isNative || initialized || !firebaseUser?.uid) return

    const platform = Capacitor.getPlatform();
    const RC_IOS: string | undefined = process.env.REACT_APP_REVENUECAT_API_KEY_IOS;
    const RC_ANDROID: string | undefined = process.env.REACT_APP_REVENUECAT_API_KEY_ANDROID;
    const rcKey = platform === 'android' ? RC_ANDROID : RC_IOS;
    if (!rcKey) {
      console.error('Missing RevenueCat API key for', platform);
      return;
    }

    (async () => {
      // —————————————————————
      // 1) CONFIGURE SDK
      // —————————————————————
      try {
        // Optional: crank up logs while integrating
        try { await Purchases.setLogLevel?.({ level: 'DEBUG' as any }) } catch {}

        await Purchases.configure({ apiKey: rcKey, appUserID: firebaseUser.uid })
      } catch (err) {
        console.error('⚠️ RevenueCat configure failed:', err)
        return
      }

      // —————————————————————
      // 2) LINK BACKEND (fire-and-forget)
      // —————————————————————
      fetchWithAuth('/api/subscription/link-revenuecat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rcAppUserId: firebaseUser.uid }),
      }).catch((err) => {
        console.warn('⚠️ Could not link RC user to backend:', err)
      })

      // —————————————————————
      // 3) FETCH OFFERINGS & CUSTOMER INFO
      // —————————————————————
      try {
        const off = await Purchases.getOfferings()
        setOfferings(off)

        const { customerInfo: info } = await Purchases.getCustomerInfo()
        setCustomerInfo(info)

        // —————————————————————
        // 4) READY → show paywall
        // —————————————————————
        setInitialized(true)
      } catch (err) {
        console.error('⚠️ RevenueCat data load failed:', err)
      }
    })()
  }, [initialized, firebaseUser.uid])

  // Fetch updated offerings
  const getOfferings = useCallback(async () => {
    if (!isNative) return null
    const off = await Purchases.getOfferings()
    setOfferings(off)
    return off
  }, [])

  // Purchase a package
  const purchasePackage = useCallback(
    async (pkg: PurchasesPackage) => {
      if (!isNative) throw new Error('Native only')
      const result = await Purchases.purchasePackage({ aPackage: pkg })
      const info = result.customerInfo
      setCustomerInfo(info)
      return info
    },
    []
  )

  // Restore previous purchases
  const restorePurchases = useCallback(async () => {
    if (!isNative) return null
    const { customerInfo: info } = await Purchases.restorePurchases()
    setCustomerInfo(info)
    return info
  }, [])

  // Open native subscription-management UI
  const showManageSubscriptions = useCallback(async () => {
    if (!isNative) return
    await RevenueCatUI.presentCustomerCenter()
  }, [])

  return {
    initialized: isNative && initialized,
    offerings,
    customerInfo,
    getOfferings,
    purchasePackage,
    restorePurchases,
    showManageSubscriptions,
  }
}
